import React from 'react';
import { Link } from 'react-router-dom';
import { Home, ArrowLeft, Search } from 'lucide-react';
import { usePageTitle } from '../hooks/usePageTitle';
import { useMetaDescription } from '../hooks/useMetaDescription';
import { APP_NAME } from '../config/branding';

const NotFound: React.FC = () => {
  usePageTitle('404 - Page Not Found');
  useMetaDescription(`The page you are looking for could not be found. Return to ${APP_NAME} homepage or browse our veterinary AI products.`);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center px-4">
      <div className="max-w-2xl w-full text-center">
        {/* 404 Logo */}
        <div className="mb-8">
          <img 
            src="/logo_clear_back.png" 
            alt={`${APP_NAME} Logo`}
            width="120" 
            height="120" 
            className="mx-auto mb-6"
          />
        </div>

        {/* 404 Number */}
        <div className="mb-8">
          <h1 className="text-9xl font-bold text-gray-800" style={{
            fontFamily: 'Calibre, ui-sans-serif, system-ui, -apple-system, "system-ui", "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif',
            letterSpacing: '-0.05em'
          }}>
            404
          </h1>
        </div>

        {/* Error Message */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Page Not Found
          </h2>
          <p className="text-lg text-gray-600 max-w-md mx-auto">
            The page you're looking for doesn't exist or has been moved. 
            Let's get you back on track.
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
          <Link 
            to="/"
            className="inline-flex items-center gap-2 px-6 py-3 bg-gray-800 text-white rounded-lg font-medium hover:bg-gray-900 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
          >
            <Home className="w-5 h-5" />
            Go to Homepage
          </Link>
          
          <button 
            onClick={() => window.history.back()}
            className="inline-flex items-center gap-2 px-6 py-3 bg-white text-gray-800 border-2 border-gray-300 rounded-lg font-medium hover:bg-gray-50 transition-all duration-200"
          >
            <ArrowLeft className="w-5 h-5" />
            Go Back
          </button>
        </div>

        {/* Helpful Links */}
        <div className="mt-16 pt-8 border-t border-gray-200">
          <h3 className="text-sm font-semibold text-gray-700 mb-4 uppercase tracking-wider">
            Popular Pages
          </h3>
          <div className="flex flex-wrap justify-center gap-4 text-sm">
            <Link to="/products/ai-front-desk" className="text-gray-600 hover:text-gray-900 underline">
              AI Front Desk
            </Link>
            <Link to="/products/visit-transcriptions" className="text-gray-600 hover:text-gray-900 underline">
              Visit Transcriptions
            </Link>
            <Link to="/pricing" className="text-gray-600 hover:text-gray-900 underline">
              Pricing
            </Link>
            <Link to="/practices" className="text-gray-600 hover:text-gray-900 underline">
              Find Practices
            </Link>
            <Link to="/contact" className="text-gray-600 hover:text-gray-900 underline">
              Contact Us
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotFound;

